import { i18n } from '@/locale'

import moment from 'moment'
import camelcaseKeys from 'camelcase-keys'

import { currencyTypes } from '@/config'
import { formatAmount } from '@/lib/helpers/MoneyFormat'
import { enumerateDaysBetweenDates } from '@/lib/helpers/Time'
import { getGuestName, getGuestLastName } from '@/lib/helpers/GuestName'

import { MeRequests } from '@/services/me.requests'

const meService = new MeRequests()

const state = {
  bookings: null,
  currentAccount: null,
  settings: {},
  facility: null,
  registrationCards: null,
  currencies: [],
}

const getters = {
  validBookings: (state, getters) => {
    return !!getters.firstCheckIn && !!getters.lastCheckOut
  },
  firstCheckIn: state => {
    if (!state.bookings) {
      return null
    } else {
      let checkIn
      state.bookings.forEach(booking => {
        if (!booking.booking.checkin) {
          checkIn = null
        } else if (checkIn === undefined) {
          checkIn = booking.booking.checkin
        } else if (!!checkIn && booking.booking.checkin && moment(booking.booking.checkin).isBefore(checkIn)) {
          checkIn = booking.booking.checkin
        }
      })
      return checkIn
    }
  },
  firstAvailableDate: (state, getters) => {
    if (getters.firstCheckIn && moment(getters.firstCheckIn).isAfter(moment())) {
      return moment(getters.firstCheckIn).startOf('day').format('YYYY-MM-DD')
    } else {
      return moment().format('YYYY-MM-DD')
    }
  },
  lastCheckOut: state => {
    if (!state.bookings) {
      return null
    } else {
      let checkOut
      state.bookings.forEach(booking => {
        if (!booking.booking.checkout) {
          checkOut = null
        } else if (checkOut === undefined) {
          checkOut = booking.booking.checkout
        } else if (!!checkOut && booking.booking.checkout && moment(booking.booking.checkout).isAfter(checkOut)) {
          checkOut = booking.booking.checkout
        }
      })
      return checkOut
    }
  },
  lastAvailableDate: (state, getters) => {
    if (!getters.lastCheckOut) return moment().endOf('day').add(1, 'y').format('YYYY-MM-DD')
    return getters.lastCheckOut
      ? moment(getters.lastCheckOut).endOf('day').format('YYYY-MM-DD')
      : getters.firstCheckIn
        ? moment(getters.firstCheckIn).endOf('day').add(1, 'y').format('YYYY-MM-DD')
        : moment().endOf('day').add(1, 'y').format('YYYY-MM-DD')
  },
  guests: state => {
    if (!state.bookings) {
      return []
    }
    var guests = []
    state.bookings.forEach(booking => {
      booking.guests.forEach(guest => {
        if (!guest.guest.booking) {
          guest.guest.booking = {
            accountId: booking.booking.accountId,
          }
        }
        if (!guest.guest.room && guest.guest.roomId && booking.rooms.length) {
          guest.guest.room = booking.rooms.find((room) => { return room.room.id === guest.guest.roomId })
        }
        guests.push(guest)
      })
    })
    return guests
  },
  allowedLanguageCodes: state => {
    if (!state.facility) {
      return []
    }
    return state.facility.allowedLanguageCodes
  },
  defaultLanguageCode: state => {
    if (!state.facility) {
      return null
    }
    return state.facility.defaultLanguageCode
  },
  accounts: state => {
    if (!state.bookings) {
      return null
    }
    var accounts = []
    state.bookings.forEach(booking => {
      const getIdAccount = function (accountId) {
        return `(${ accountId.substr(accountId.length - 7, accountId.length) })`
      }

      if (booking.booking.accountId !== null) {
        accounts.push({
          id: booking.booking.accountId,
          name: `${ i18n.t('account_options.booking') } ${ getIdAccount(booking.booking.accountId) })`,
        })
      }

      booking.rooms.forEach(room => {
        if (room.accountId !== null && !accounts.find((a) => { return a.id === room.accountId })) {
          accounts.push({
            id: room.accountId,
            name: i18n.t('account_options.room') + `: ${ room.room.name } ${ getIdAccount(room.accountId) }`,
          })
        }
      })

      booking.guests.forEach(guest => {
        if (guest.guest.accountId !== null && !accounts.find((a) => { return a.id === guest.guest.accountId })) {
          accounts.push({
            id: guest.guest.accountId,
            name: i18n.t('account_options.guest') + `: ${ getGuestName(guest) } ${ getGuestLastName(guest) } ${ getIdAccount(guest.guest.accountId) }`,
          })
        }
      })
    })
    state.currentAccount = accounts[0]
    return accounts
  },
  roomsByBooking: state => {
    if (state.bookings === null) {
      return []
    }
    var roomsByBooking = []
    state.bookings.forEach(decoratedBooking => {
      let guests = decoratedBooking.guests
      const rooms = decoratedBooking.rooms.map(function (roomWithAccount) {
        let guestsByRoom = guests.filter(guest => guest.guest.roomId === roomWithAccount.room.id).map((g) => {
          g.registrationCard = state.registrationCards ? state.registrationCards.find((rC) => {
            return rC.guestId === g.guest.id
          }) : null
          return g
        })
        return {
          name: roomWithAccount.room.name,
          guests: guestsByRoom,
        }
      })
      const guestsWithoutRoom = decoratedBooking.guests.filter(guest => guest.guest.roomId === null).map((g) => {
        g.registrationCard = state.registrationCards ? state.registrationCards.find((rC) => {
          return rC.guestId === g.guest.id
        }) : null
        return g
      })
      roomsByBooking.push({ booking: decoratedBooking.booking, rooms, guestsWithoutRoom })
    })
    return roomsByBooking
  },
  allRooms: state => {
    if (!state.bookings) {
      return []
    }
    var rooms = []
    state.bookings.forEach(booking => {
      booking.rooms.forEach(room => {
        rooms.push(room)
      })
    })
    return rooms
  },
  baseCurrency: state => {
    return state.currencies.find(({ currency }) => currency.id === state.facility.baseCurrencyId)
  },
  facilityTimeZone: state => {
    return state.facility.timezone
  },
  displayCurrency: state => {
    return state.currencies.find(({ currency }) => currency.id === state.settings.displayCurrencyId)
  },
  showDisplayCurrency: (state, getters) => {
    return getters.displayCurrency && getters.displayCurrency.currency.id !== getters.baseCurrency.currency.id
  },
  getPriceString: (state, getters) => ({ amountAndCurrency, amount, currency, displayCurrency = getters.baseCurrency }) => {
    let price = amountAndCurrency ? amountAndCurrency.amount : amount
    let curr = amountAndCurrency ? amountAndCurrency.currency : currency
    let currencyName = curr.currency ? curr.currency.displayName || curr.currency.name : curr.displayName || curr.name
    let currencyType = curr.currency ? curr.currency.type : curr.type

    if (displayCurrency && displayCurrency.id !== (curr.id || curr.currency.id)) {
      price = price / displayCurrency.exchangeRate
      currencyName = displayCurrency.currency.displayName || displayCurrency.currency.name
    }
    return `${ currencyType !== currencyTypes.LEGAL ? price : formatAmount(price) } ${ currencyName }`
  },
  isDateInSomeBooking: (state, getters) => date /* YYYY-MM-DD */ => {
    return state.bookings.some(({ booking }) => {
      const { checkin, checkout } = booking
      return (
        !checkout && !checkin
          ? true
          : !checkout
            ? moment(date, 'YYYY-MM-DD').isSameOrAfter(checkin, 'day')
            : !checkin
              ? moment(date, 'YYYY-MM-DD').isSameOrBefore(checkout, 'day')
              // !!checkin && !!checkout
              : moment(date, 'YYYY-MM-DD').isBetween(checkin, checkout, 'day', [])
      ) && moment(getters.firstCheckIn).isSameOrBefore(date, 'day')
    })
  },
  isDateTimeInSomeBooking: (state, getters) => dateTime => {
    return state.bookings.some(({ booking }) => {
      const { checkin, checkout } = booking
      return (
        !checkout && !checkin
          ? true
          : !checkout
            ? moment(dateTime).isSameOrAfter(checkin)
            : !checkin
              ? moment(dateTime).isSameOrBefore(checkout)
              // !!checkin && !!checkout
              : moment(dateTime).isBetween(checkin, checkout, undefined, [])
      ) && moment(getters.firstCheckIn).isSameOrBefore(dateTime)
    })
  },
  availableDates: (state, getters) => {
    if (!getters.validBookings) return []
    return enumerateDaysBetweenDates(getters.firstCheckIn, getters.lastCheckOut, true).filter(date => {
      return getters.isDateInSomeBooking(date) &&
        moment(date, 'YYYY-MM-DD').isSameOrAfter(getters.firstAvailableDate, 'day')
    })
  },
}

const actions = {
  getBookings ({ commit }) {
    return new Promise((resolve, reject) => {
      meService.getBookings().then((bookings) => {
        commit('setBookings', bookings)
        resolve(bookings)
      }).catch((error) => {
        reject(error)
      })
    })
  },
  getSettings ({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      dispatch('listCurrencies')
      meService.getSettings().then(async (settings) => {
        state.facility = await dispatch('getFacility')
        commit('setSettings', settings)
        resolve(settings)
      }).catch((error) => {
        reject(error)
      })
    })
  },
  getFacility ({ state }) {
    return new Promise((resolve, reject) => {
      meService.getFacility().then((facility) => {
        state.facility = facility
        resolve(facility)
      }).catch((error) => {
        reject(error)
      })
    })
  },
  putSettings ({ dispatch }, { email, languageCode, displayCurrencyId }) {
    return new Promise((resolve, reject) => {
      meService.putSettings(email, languageCode, displayCurrencyId).then(() => {
        dispatch('getSettings')
        resolve()
      }).catch((error) => {
        reject(error)
      })
    })
  },
  putPassword ({ dispatch }, { oldPassword, newPassword }) {
    return new Promise((resolve, reject) => {
      meService.putPassword(oldPassword, newPassword).then(() => {
        dispatch('getSettings')
        resolve()
      }).catch((error) => {
        reject(error)
      })
    })
  },
  getRegistrationCards ({ commit }) {
    return new Promise((resolve, reject) => {
      meService.getRegistrationCards().then((registrationCards) => {
        commit('setRegistrationCards', camelcaseKeys(registrationCards, { deep: true }))
        resolve(registrationCards)
      }).catch((error) => {
        reject(error)
      })
    })
  },
  listCurrencies ({ state }) {
    return new Promise((resolve, reject) => {
      meService.listCurrencies().then((currencies) => {
        state.currencies = currencies
        resolve(currencies)
      }).catch((error) => {
        reject(error)
      })
    })
  },
}

const mutations = {
  setBookings (state, bookings) {
    state.bookings = bookings
  },
  setSettings (state, settings) {
    state.settings = settings
  },
  setRegistrationCards (state, registrationCards) {
    state.registrationCards = registrationCards
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
